import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { EmptyState } from '@/components/common/EmptyState'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Quote = Tables<'quotes'> & { organizations: { name: string } | null }

const STATUSES = ['all', 'draft', 'sent', 'accepted', 'rejected', 'expired', 'converted'] as const

export function AdminQuotesListPage() {
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  useEffect(() => {
    async function load() {
      let query = supabase.from('quotes').select('*, organizations(name)')
        .order('created_at', { ascending: false })
      if (statusFilter !== 'all') query = query.eq('status', statusFilter as Tables<'quotes'>['status'])
      const { data } = await query
      setQuotes((data as unknown as Quote[]) || [])
      setLoading(false)
    }
    setLoading(true)
    load()
  }, [statusFilter])

  return (
    <div>
      <PageHeader title="Quotes" description="View all quotes across distributors" />

      <div className={css.tabs} style={{ marginBottom: 'var(--space-6)' }}>
        {STATUSES.map(s => (
          <button key={s} className={`${css.tab} ${statusFilter === s ? css.tabActive : ''}`} onClick={() => setStatusFilter(s)}>
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {loading ? <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p> : quotes.length === 0 ? (
        <EmptyState title="No quotes found" description="Quotes will appear here when distributors create them" />
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Organization</th>
                <th>Version</th>
                <th>Status</th>
                <th>Total</th>
                <th>Valid Until</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map(q => (
                <tr key={q.id} className={css.tableClickableRow} onClick={() => navigate(`/admin/quotes/${q.id}`)}>
                  <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{q.reference_code}</code></td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{q.organizations?.name || '—'}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>v{q.version}</td>
                  <td><StatusBadge label={q.status} /></td>
                  <td style={{ fontWeight: 500 }}>£{Number(q.total).toLocaleString()}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>
                    {q.valid_until ? new Date(q.valid_until).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>{new Date(q.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
